import localizeCurrency from "@helpers/localizeCurrency"
import localizeDate from "@helpers/localizeDate"
import { getOnboardingContext } from "@helpers/localStorage"
import { PaymentIntent } from "@stripe/stripe-js"
import { addMonths, addWeeks, addYears } from "date-fns"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { Plan } from "src/types"

interface OrderSummaryProps {
  fullPrice: number
  paymentIntent: PaymentIntent | undefined
  plan: Plan
}

export default function OrderSummary({
  fullPrice,
  paymentIntent,
  plan,
}: OrderSummaryProps) {
  const { t } = useTranslation()
  const context = getOnboardingContext()
  const paymentCopyKey =
    context === "cars"
      ? "pages.checkout.paymentCars"
      : context === "pets"
        ? "pages.checkout.paymentPets"
        : "pages.checkout.payment"

  const isOneTime = plan === "one-time"

  const renewalDate = useMemo(() => {
    if (isOneTime) return null
    switch (plan) {
      case "weekly":
        return addWeeks(new Date(), 1)
      case "monthly":
        return addMonths(new Date(), 1)
      case "quarterly":
        return addMonths(new Date(), 3)
      case "yearly":
        return addYears(new Date(), 1)
    }
  }, [plan, isOneTime])

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold">
        {t(`${paymentCopyKey}.orderSummary.orderSummary`)}
      </h2>

      <div className="space-y-4 rounded-2xl bg-white p-5 text-black">
        <div className="space-y-2 text-sm">
          <p className="font-bold">
            {t(`${paymentCopyKey}.orderSummary.fullaccessHeadline`)}
          </p>

          <p>{t(`${paymentCopyKey}.orderSummary.fullaccessContent`)}</p>
        </div>

        {!isOneTime && (
          <div className="space-y-2 text-sm">
            <p className="font-bold">
              {t(`${paymentCopyKey}.orderSummary.durationHeadline`)}
            </p>

            <p>
              {(() => {
                switch (plan) {
                  case "weekly":
                    return t(`${paymentCopyKey}.orderSummary.durationWeek`, {
                      count: 1,
                    })
                  case "monthly":
                    return t(`${paymentCopyKey}.orderSummary.durationMonth`, {
                      count: 1,
                    })
                  case "quarterly":
                    return t(`${paymentCopyKey}.orderSummary.durationMonth`, {
                      count: 3,
                    })
                  case "yearly":
                    return t(`${paymentCopyKey}.orderSummary.durationYear`, {
                      count: 1,
                    })
                }
              })()}
            </p>
          </div>
        )}

        {!isOneTime && renewalDate && (
          <div className="space-y-2 text-sm">
            <p className="font-bold">
              {t(`${paymentCopyKey}.orderSummary.renewalHeadline`)}
            </p>

            <p>{localizeDate(renewalDate)}</p>

            <p>{t(`${paymentCopyKey}.orderSummary.renewalDisclaimer`)}</p>
          </div>
        )}

        {isOneTime && (
          <div className="space-y-2 text-sm">
            <p className="font-bold">
              {t(`${paymentCopyKey}.orderSummary.oneTimeNotice`)}
            </p>
          </div>
        )}

        {paymentIntent && (
          <div className="space-y-2">
            {(() => {
              if (isOneTime) {
                return (
                  <p className="font-bold">
                    {t(`${paymentCopyKey}.oneTimeTotal`, {
                      price: localizeCurrency({
                        amountInCents: paymentIntent.amount,
                        currency: paymentIntent.currency,
                      }),
                    })}
                  </p>
                )
              }

              const thisPeriodKey =
                plan === "weekly"
                  ? "thisWeek"
                  : plan === "monthly"
                    ? "thisMonth"
                    : plan === "quarterly"
                      ? "thisQuarter"
                      : "thisYear"

              const specialOfferKey =
                plan === "weekly"
                  ? "firstWeekSpecialOffer"
                  : plan === "monthly"
                    ? "firstMonthSpecialOffer"
                    : plan === "quarterly"
                      ? "firstQuarterSpecialOffer"
                      : "firstYearSpecialOffer"

              if (paymentIntent.amount !== fullPrice) {
                return (
                  <>
                    <p className="line-through">
                      {t(`${paymentCopyKey}.${thisPeriodKey}`, {
                        price: localizeCurrency({
                          amountInCents: fullPrice,
                          currency: paymentIntent.currency,
                        }),
                      })}
                    </p>

                    <p className="font-bold">
                      {t(`${paymentCopyKey}.${specialOfferKey}`, {
                        price: localizeCurrency({
                          amountInCents: paymentIntent.amount,
                          currency: paymentIntent.currency,
                        }),
                      })}
                    </p>
                  </>
                )
              }

              return (
                <p className="font-bold">
                  {t(`${paymentCopyKey}.${thisPeriodKey}`, {
                    price: localizeCurrency({
                      amountInCents: paymentIntent.amount,
                      currency: paymentIntent.currency,
                    }),
                  })}
                </p>
              )
            })()}
          </div>
        )}
      </div>
    </div>
  )
}
